/**
 * The accounts screen — the Disciplinary Section administers its own factory.
 *
 * Three things here are not decoration.
 *
 * **Every command asks for the officer's own password.** Not because the session
 * is untrusted, but because administering an account should be a deliberate act
 * rather than a click, and because it proves who is at the keyboard. This
 * replaced a shared `operator` login that was designed and dropped: a password
 * three or four people know proves only that somebody knew it, and the audit
 * trail would have said `op` where it can now say a name.
 *
 * **The failed sign-in count is shown, and null is not zero.** `login_failures`
 * is null when Redis could not be reached, and rendering that as "none" would
 * tell an officer there have been no attempts on a name at exactly the moment
 * they are about to hand over a new password for it. Null renders as "not
 * checked".
 *
 * **A refused action stays on the screen with its reason.** A CGM reaching this
 * page sees why it is closed rather than a blank, and the sentence comes off the
 * server's refusal — `CP-ROLE-01` — so the screen and the guard cannot drift.
 *
 * Nothing here composes a rule of its own. The client draws what the server
 * says, and every one of these calls is refused again on the server if the role
 * may not make it.
 */

import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../api/client'
import type { AccountView } from '../api/client'
import { ErrorEnvelope } from '../components/bits'
import { useSession } from '../lib/session'

type Dialog =
  | { kind: 'create' }
  | { kind: 'reset'; username: string }
  | { kind: 'close'; username: string }
  | { kind: 'reopen'; username: string }
  | { kind: 'lockout'; username: string }
  | null

export function Accounts() {
  const { user } = useSession()
  const queryClient = useQueryClient()
  const [dialog, setDialog] = useState<Dialog>(null)

  // A null role is unknown, never "officer" — the field's own contract. An
  // account whose role this client cannot read gets the closed door, not the
  // benefit of the doubt.
  const mayAdminister = user?.role === 'ds_officer'

  const accounts = useQuery({
    queryKey: ['accounts'],
    queryFn: () => api.listAccounts(),
    enabled: mayAdminister,
  })

  if (!mayAdminister) {
    return (
      <div className="page">
        <header>
          <h1>Accounts</h1>
        </header>
        <p className="muted">
          {user?.role === 'cgm'
            ? 'This account reads and asks. Accounts are administered by the Disciplinary Section. '
            : "This account's role is not known to this client, so account actions are not offered. "}
          <Link to="/cases">back to the register</Link>.
        </p>
        <p className="small muted">
          You can still <Link to="/account/password">change your own password</Link>.
        </p>
      </div>
    )
  }

  return (
    <div className="page">
      <header>
        <h1>Accounts</h1>
        <div className="small muted">
          {user?.unit_name} only. Accounts at other factories are not reachable from here — not
          filtered out of this list, but invisible to it.
        </div>
      </header>

      <div className="row">
        <button type="button" onClick={() => setDialog({ kind: 'create' })}>
          Add account
        </button>
        <Link className="quiet" to="/account/password">
          Change my password
        </Link>
      </div>

      {accounts.isError ? <ErrorEnvelope error={accounts.error} /> : null}

      <table className="rows">
        <thead>
          <tr>
            <th>Username</th>
            <th>Name</th>
            <th>Post</th>
            <th>Role</th>
            <th>Sign-ins</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {(accounts.data ?? []).map((row) => (
            <AccountRow
              key={row.username}
              row={row}
              isSelf={row.username === user?.username}
              onAct={setDialog}
            />
          ))}
        </tbody>
      </table>

      {dialog ? (
        <CommandDialog
          dialog={dialog}
          onDone={() => {
            setDialog(null)
            queryClient.invalidateQueries({ queryKey: ['accounts'] })
          }}
          onCancel={() => setDialog(null)}
        />
      ) : null}
    </div>
  )
}

function AccountRow({
  row,
  isSelf,
  onAct,
}: {
  row: AccountView
  isSelf: boolean
  onAct: (dialog: Dialog) => void
}) {
  return (
    <tr className={row.is_active ? undefined : 'muted'}>
      <td className="mono">{row.username}</td>
      <td>{row.display_name}</td>
      <td>{row.post ?? '—'}</td>
      <td className="small">{row.role}</td>
      <td className="small">
        {/* Null is "not checked", never "none". An officer told there have been
            no failed attempts, when nothing was actually asked, is misinformed
            at the worst possible moment. */}
        {row.login_failures === null ? (
          <span className="muted">not checked</span>
        ) : row.login_failures === 0 ? (
          <span className="muted">—</span>
        ) : (
          <>
            <strong>{row.login_failures} failed</strong>{' '}
            <button
              type="button"
              className="quiet"
              onClick={() => onAct({ kind: 'lockout', username: row.username })}
            >
              clear
            </button>
          </>
        )}
      </td>
      <td className="right">
        {row.is_active ? (
          <>
            <button
              type="button"
              className="quiet"
              onClick={() => onAct({ kind: 'reset', username: row.username })}
            >
              Reset password
            </button>{' '}
            <button
              type="button"
              className="quiet"
              onClick={() => onAct({ kind: 'close', username: row.username })}
              disabled={isSelf}
              title={isSelf ? 'You cannot close the account you are signed in as.' : undefined}
            >
              Close
            </button>
          </>
        ) : (
          <button
            type="button"
            className="quiet"
            onClick={() => onAct({ kind: 'reopen', username: row.username })}
          >
            Reopen
          </button>
        )}
      </td>
    </tr>
  )
}

/**
 * One dialog for all four commands, because all four end the same way: the
 * officer confirms with their own password and the server decides.
 */
function CommandDialog({
  dialog,
  onDone,
  onCancel,
}: {
  dialog: NonNullable<Dialog>
  onDone: () => void
  onCancel: () => void
}) {
  const [username, setUsername] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [post, setPost] = useState('')
  const [role, setRole] = useState('ds_officer')
  const [password, setPassword] = useState('')
  const [again, setAgain] = useState('')
  const [ownPassword, setOwnPassword] = useState('')

  const needsNewPassword = dialog.kind === 'create' || dialog.kind === 'reset'
  const mismatched = needsNewPassword && password !== again

  const run = useMutation({
    // Four commands, four different response shapes, and the screen cares about
    // none of them — it refetches the roster either way. Typed to void so the
    // union does not have to be reconciled for a value nobody reads.
    mutationFn: async (): Promise<void> => {
      if (dialog.kind === 'create') {
        await api.createAccount({
          username,
          password,
          display_name: displayName || null,
          post: post || null,
          role,
          own_password: ownPassword,
        })
        return
      }
      if (dialog.kind === 'reset') {
        await api.setAccountPassword(dialog.username, {
          password,
          own_password: ownPassword,
        })
        return
      }
      if (dialog.kind === 'close') {
        await api.deactivateAccount(dialog.username, { own_password: ownPassword })
        return
      }
      if (dialog.kind === 'reopen') {
        await api.reactivateAccount(dialog.username, { own_password: ownPassword })
        return
      }
      await api.clearAccountLockout(dialog.username, { own_password: ownPassword })
    },
    onSuccess: onDone,
  })

  const title =
    dialog.kind === 'create'
      ? 'Add account'
      : dialog.kind === 'reset'
        ? `Reset the password for ${dialog.username}`
        : dialog.kind === 'close'
          ? `Close ${dialog.username}`
          : `Reopen ${dialog.username}`

  return (
    <>
      {/* Modal, so the scrim is part of it: the page behind holds nothing the
          officer needs while filling this in, and dimming it says so. Clicking
          the scrim cancels, which is the same act as Cancel — no field here is
          expensive enough to defend with a confirm-you-want-to-discard. */}
      <div className="scrim" onClick={onCancel} />
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onKeyDown={(e) => {
          if (e.key === 'Escape') onCancel()
        }}
      >
        <h2>{title}</h2>

        {dialog.kind === 'create' ? (
          <>
            <label>
              Username
              <input value={username} onChange={(e) => setUsername(e.target.value)} />
            </label>
            <p className="small muted">
              Permanent. Accounts are closed, never deleted, and a username is never reused —
              the audit trail records who acted by this name.
            </p>
            <label>
              Name
              <input value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
            </label>
            <label>
              Post
              <input value={post} onChange={(e) => setPost(e.target.value)} />
            </label>
            <label>
              Role
              {/* Named explicitly: a `select` inside a `label` takes its
                accessible name from the label's whole text content, options
                included, so without this a screen reader announces "Role
                Disciplinary Section officer CGM — reads and asks" before
                saying what the control is. */}
              <select aria-label="Role" value={role} onChange={(e) => setRole(e.target.value)}>
                <option value="ds_officer">Disciplinary Section officer</option>
                <option value="cgm">CGM — reads and asks</option>
              </select>
            </label>
          </>
        ) : null}

        {dialog.kind === 'close' ? (
          <p className="small">
            The account is closed, not deleted. Anything it signed keeps resolving to this name,
            and it can be reopened.
          </p>
        ) : null}

        {dialog.kind === 'lockout' ? (
          <p className="small">
            This drops the record of failed sign-ins against this name. It is the only standing
            sign that somebody has been trying passwords against it, so clear it once you have
            read it — not before.
          </p>
        ) : null}

        {dialog.kind === 'reopen' ? (
          <p className="small">
            Reopen only if this is the same person returning. A closed account handed to
            somebody new would put two officers under one username in the audit trail.
          </p>
        ) : null}

        {needsNewPassword ? (
          <>
            <label>
              New password
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </label>
            <label>
              Again
              <input type="password" value={again} onChange={(e) => setAgain(e.target.value)} />
            </label>
            <p className="small muted">
              At least 12 characters. No leading or trailing spaces.
            </p>
          </>
        ) : null}

        {dialog.kind === 'reset' ? (
          <p className="small">
            This signs {dialog.username} out of every session they have open, on every machine.
          </p>
        ) : null}

        <label>
          Confirm with your own password
          <input
            type="password"
            value={ownPassword}
            onChange={(e) => setOwnPassword(e.target.value)}
          />
        </label>

        {mismatched && again ? <p className="small">The two entries do not match.</p> : null}
        {run.isError ? <ErrorEnvelope error={run.error} /> : null}

        <div className="row">
          <button
            type="button"
            onClick={() => run.mutate()}
            disabled={run.isPending || mismatched || !ownPassword}
          >
            {run.isPending ? 'Working…' : title}
          </button>
          <button type="button" className="quiet" onClick={onCancel}>
            Cancel
          </button>
        </div>
      </div>
    </>
  )
}

/**
 * Changing your own password. **Every role reaches this**, the CGM included — a
 * factory head who had to ask a section officer to change their own password
 * would be an oversight, not a boundary.
 *
 * Asks for the current password rather than a re-entry, because that is what
 * proves the change is the account holder's and not a passer-by's at a desk
 * somebody walked away from.
 */
export function ChangeMyPassword() {
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [again, setAgain] = useState('')
  const [done, setDone] = useState(false)

  const change = useMutation({
    mutationFn: () => api.changeOwnPassword({ current_password: current, new_password: next }),
    onSuccess: () => {
      setDone(true)
      setCurrent('')
      setNext('')
      setAgain('')
    },
  })

  return (
    <div className="page">
      <header>
        <h1>Change my password</h1>
      </header>

      {done ? (
        <p>
          Changed. You are still signed in here; every other session on this account has ended.{' '}
          <Link to="/cases">back to the register</Link>.
        </p>
      ) : null}

      <label>
        Current password
        <input type="password" value={current} onChange={(e) => setCurrent(e.target.value)} />
      </label>
      <label>
        New password
        <input type="password" value={next} onChange={(e) => setNext(e.target.value)} />
      </label>
      <label>
        Again
        <input type="password" value={again} onChange={(e) => setAgain(e.target.value)} />
      </label>
      <p className="small muted">
        At least 12 characters. No leading or trailing spaces. Changing it signs out every other
        session on this account and leaves this one alone.
      </p>

      {next !== again && again ? <p className="small">The two entries do not match.</p> : null}
      {change.isError ? <ErrorEnvelope error={change.error} /> : null}

      <div className="row">
        <button
          type="button"
          onClick={() => change.mutate()}
          disabled={change.isPending || !current || !next || next !== again}
        >
          {change.isPending ? 'Working…' : 'Change password'}
        </button>
      </div>
    </div>
  )
}
